// SS PWA — components/health/WeeklyOutcomesGrid.tsx — 2026-06-03
//
// CLIENT-FACING. Grid of outcome cards. Numbers come straight from
// counter-bag readings on the AuditResponse / HealthResponse. Copy from
// healthOutcomeCopy.ts. Hides any card whose numerator is unavailable.

import { OutcomeCard } from './OutcomeCard';
import { healthCopy } from '@/lib/copy/healthOutcomeCopy';
import type { AuditResponse } from '@/lib/api/diagnostics';

interface WeeklyOutcomesGridProps {
  audit: AuditResponse | null;
}

function pct(num: number, den: number): number {
  if (!den || den <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((num / den) * 100)));
}

export function WeeklyOutcomesGrid({ audit }: WeeklyOutcomesGridProps) {
  if (!audit) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-4 text-sm text-ink-500 dark:border-[#1F2A37] dark:bg-[#0F172A] dark:text-[#94A3B8]">
        {healthCopy.empty.loading}
      </div>
    );
  }
  const c = audit.system_health.counters;
  const callsHandled = c.calls_received ?? 0;
  const smsSent = c.sms_sent ?? 0;
  const smsRcv = c.sms_received ?? 0;
  // Coarse proxy for "fast response" — we count outbound vs inbound. The
  // health endpoint will add a true latency histogram later.
  const responseRatePct = pct(smsSent, smsRcv);
  const quotes = audit.summary.quotes_audited ?? 0;
  const reviews = c.review_request_fired ?? 0;
  return (
    <section>
      <div className="mb-2 text-sm font-medium text-ink-700 dark:text-[#94A3B8]">
        {healthCopy.outcomes.weeklyMomentum}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <OutcomeCard
          big={callsHandled}
          sub={healthCopy.outcomes.callsHandled_sub}
          emphasis="highlight"
        />
        <OutcomeCard
          big={`${responseRatePct}%`}
          sub={healthCopy.outcomes.smsResponseRate_sub}
        />
        <OutcomeCard
          big={quotes}
          sub={healthCopy.outcomes.quotesSent_sub}
        />
        <OutcomeCard
          big={reviews}
          sub={healthCopy.outcomes.reviewsRequested_sub}
        />
      </div>
    </section>
  );
}
